import {
  ADMIN_LABEL_VARIANT_NAMES,
  type HotelCardProps,
  type LabelVariantValue,
  type MapHotelCardProps,
} from 'src/components/HotelCard/types';
import { i18n } from '@lt/localization';
import type { ILabel } from '@lt/api/dist/methods/packages/types';
import { addDays, isValid, parseISO } from 'date-fns';
import { formatDate } from '@lt/components/utils/date';
import { pluralizeNights } from 'src/utils/pluralizeUtils';
import type { IHotelRendererProps } from './types';

/**
 * Эта функция разделяет лейблы по месту на карточке, где они должны отображаться
 * и приводит данные по лейблам к единому формату
 */
export const getHotelCardLabels = (
  labels: ILabel[],
): {
  innerLabels: LabelVariantValue[];
  headerLabel?: ILabel;
} => {
  const headerLabel = labels.find((label) => label.context?.search === 1);
  const innerLabels: LabelVariantValue[] = labels
    .filter((label) => label.context?.search === 2)
    .map((el) => ({
      id: String(el.id),
      variant: ADMIN_LABEL_VARIANT_NAMES[el.style],
      tooltipText: el.description,
      text: el.title,
    }));

  return {
    headerLabel,
    innerLabels: innerLabels.slice(0, 6),
  };
};

export const getDatesInfo = (
  startDate: string,
  nightsCount: number,
  isShortNight = false,
) => {
  const dateAsDate = parseISO(startDate);
  const formattedStartDate = isValid(dateAsDate)
    ? formatDate(dateAsDate, 'd MMM')
    : startDate;
  const endDate = addDays(new Date(startDate), nightsCount);
  const formattedEndDate = isValid(endDate)
    ? formatDate(endDate, 'd MMM')
    : endDate;

  return `${formattedStartDate} - ${formattedEndDate}, ${nightsCount} ${
    isShortNight
      ? i18n.t('hotelcard.dates.night')
      : pluralizeNights(nightsCount)
  }`;
};

// TODO: типизировать параметр после переписывания и типизации стора hotels (https://tracker.yandex.ru/LT-40716)
export const getHotelFeatures = (features) => {
  const { airport_distance, beach_distance, beach_surface, line, wi_fi } =
    features;

  return {
    airportDistance: airport_distance,
    beachDistance: beach_distance,
    beachSurface: beach_surface,
    beachLine: line,
    wiFi: wi_fi,
  };
};

export function getHotelCardProps(
  hotelRendererProps: IHotelRendererProps,
): HotelCardProps {
  const {
    hotel,
    images,
    isViewed: hotelPageVisited,
    bonus_count: bonusCount,
    activateCashbackForPartner,
    currencyCode,
    price,
    isFlexDates,
    isMobile,
    isHotelOnly,
    openInCurrentTab,
    hotelHref,
    location,
    extras,
    availability,
    penalizedFilters = [],
    nights,
    date,
    adjacent_tours: adjacentTours,
    suggestedDates,
    labels = [],
    className,
    cashbackLabel,
    cashbackIconWebp,
    cashbackIconPng,
    cashbackTooltipText,
    labelColor,
    textColor,
  } = hotelRendererProps;

  const imagesList = images.list;

  const { id, rating, stars, name, features } = hotel;

  const { innerLabels, headerLabel } = getHotelCardLabels(labels || []);

  const adjacentToursLimit = isMobile ? 2 : 3;

  const builtAdjacentTours = adjacentTours
    ?.slice(0, adjacentToursLimit)
    .map((tour, index) => ({
      instantConfirm: tour.instant_confirm,
      price: tour.price,
      tourId: tour.tour_id,
      availability: {
        flight: String(tour.flight_seats),
        hotel: String(tour.accommodation_availability),
      },
      datesInfo: getDatesInfo(tour.start_date, tour.duration, true),
      href: suggestedDates[index].href,
    }));

  return {
    id,
    images: imagesList,
    hotelPageVisited,
    bonusCount: activateCashbackForPartner ? bonusCount : 0,
    currencyCode,
    price,
    rating: rating?.value || 0,
    stars,
    hotelName: name,
    isFlexDates,
    isHotelOnly,
    openInCurrentTab,
    className,
    innerLabels,
    headerLabel,
    hotelPageHref: hotelHref,
    hotelLocation: location,
    features: getHotelFeatures(features),
    instantConfirm: extras.instant_confirm,
    availability,
    penalizedFilters,
    datesInfo: getDatesInfo(date, nights),
    adjacentTours: builtAdjacentTours,
    cashbackLabel,
    cashbackIconWebp,
    cashbackIconPng,
    cashbackTooltipText,
    labelColor,
    textColor,
  };
}

export function getMapHotelCardProps(
  hotelRendererProps: IHotelRendererProps,
): MapHotelCardProps {
  const {
    hotel,
    images,
    isViewed: hotelPageVisited,
    bonus_count: bonusCount,
    activateCashbackForPartner,
    currencyCode,
    price,
    isHotelOnly,
    isExpanded = false,
    openInCurrentTab,
    hotelHref,
    location,
    extras,
    availability,
    labels = [],
    className,
    onClosePopup,
    cashbackLabel,
    cashbackIconWebp,
    cashbackIconPng,
    cashbackTooltipText,
    labelColor,
    textColor,
  } = hotelRendererProps;

  const imagesList = images.list;

  const { id, rating, stars, name, features } = hotel;
  const { instant_confirm: instantConfirm } = extras;

  const { innerLabels, headerLabel } = getHotelCardLabels(labels || []);

  return {
    id,
    onCloseIconClick: () => {
      onClosePopup?.();
    },
    hotelPageHref: hotelHref,
    isExpanded,
    images: imagesList,
    hotelPageVisited,
    bonusCount: activateCashbackForPartner ? bonusCount : 0,
    currencyCode,
    stars,
    hotelName: name,
    hotelLocation: location,
    rating: rating?.value || 0,
    features: getHotelFeatures(features),
    instantConfirm,
    price,
    innerLabels,
    headerLabel,
    isHotelOnly,
    availability,
    openInCurrentTab,
    className,
    cashbackLabel,
    cashbackIconWebp,
    cashbackIconPng,
    cashbackTooltipText,
    labelColor,
    textColor,
  };
}
